import { useEffect, useState } from 'react';
import { engineFetchJson } from '@/lib/engineHttp';
import type { WidgetDefinition, WidgetProps } from '@/types/widget';

interface CodexChunk {
  id: string;
  title: string | null;
  content_preview: string;
  source_type: string;
  source_uri?: string;
  trust_tier?: string;
  topic_tags?: string[];
  crystallized_at: string;
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

const TIER_COLORS: Record<string, string> = {
  SOVEREIGN: 'var(--sp-accent)',
  PROJECT: 'var(--sp-warn)',
  RESEARCH: 'var(--sp-text-muted)',
};

function CodexRecentIngestsWidget({ context }: WidgetProps) {
  const [chunks, setChunks] = useState<CodexChunk[] | null>(null);
  const [endpointMissing, setEndpointMissing] = useState(false);

  useEffect(() => {
    // Codex chunks endpoint requires a project_id. We try the codex/browse
    // endpoint first which may list chunks across projects, then fall back.
    void engineFetchJson<{ chunks?: CodexChunk[]; items?: CodexChunk[] } | CodexChunk[]>(
      '/codex/browse/recent?limit=10',
    )
      .then((data) => {
        const items: CodexChunk[] = Array.isArray(data) ? data : data.chunks ?? data.items ?? [];
        setChunks(Array.isArray(items) ? items.slice(0, 10) : []);
      })
      .catch(() => {
        setEndpointMissing(true);
        setChunks([]);
      });
  }, [context.authToken]);

  return (
    <div className="sp-widget">
      <div className="sp-widget-header">
        <span>Codex Recent Ingests</span>
        <a href="/settings/data" style={{ fontSize: 11, fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>
          Codex
        </a>
      </div>
      <div className="sp-widget-body" style={{ padding: '4px 12px' }}>
        {endpointMissing ? (
          <div>
            <div style={{ color: 'var(--sp-warn)', fontSize: 12, marginBottom: 4 }}>
              Awaiting engine endpoint
            </div>
            <div className="mono" style={{ color: 'var(--sp-text-dim)', fontSize: 11, marginBottom: 2 }}>
              GET /api/v1/codex/browse/recent
            </div>
            <div className="mono" style={{ color: 'var(--sp-text-dim)', fontSize: 11, marginBottom: 8 }}>
              (or GET /api/v1/codex/&lt;project_id&gt;/chunks)
            </div>
            {/* Mock chunk layout */}
            <div style={{ opacity: 0.35 }}>
              {[
                { src: 'claude-log', text: 'Session crystallization: auth-unify analysis...', tier: 'SOVEREIGN' },
                { src: 'readwise', text: 'Highlight from "The Art of Action" by Bungay...', tier: 'RESEARCH' },
                { src: 'youtube', text: "Nate's video transcript: scaling trust....", tier: 'PROJECT' },
              ].map((c, i) => (
                <div key={i} style={{ padding: '4px 0', fontSize: 12, borderBottom: '1px solid var(--sp-border)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span className="mono" style={{ fontSize: 9, color: TIER_COLORS[c.tier] ?? 'var(--sp-text-dim)' }}>{c.tier}</span>
                    <span className="mono" style={{ fontSize: 10, color: 'var(--sp-text-dim)' }}>{c.src}</span>
                  </div>
                  <div style={{ color: 'var(--sp-text-muted)', fontSize: 12, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {c.text}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : chunks && chunks.length === 0 ? (
          <div style={{ color: 'var(--sp-text-muted)', fontSize: 12, padding: '8px 0' }}>
            No recent Codex ingests. <a href="/sources">Connect a source</a> to start building context.
          </div>
        ) : chunks ? (
          <div style={{ maxHeight: 240, overflow: 'auto' }}>
            {chunks.map((c) => (
              <div key={c.id} style={{ padding: '4px 0', fontSize: 12, borderBottom: '1px solid var(--sp-border)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                  {c.trust_tier && (
                    <span className="mono" style={{ fontSize: 9, color: TIER_COLORS[c.trust_tier] ?? 'var(--sp-text-dim)' }}>
                      {c.trust_tier}
                    </span>
                  )}
                  <span className="mono" style={{ fontSize: 10, color: 'var(--sp-text-dim)' }}>{c.source_type}</span>
                  <span className="mono" style={{ fontSize: 10, color: 'var(--sp-text-dim)', marginLeft: 'auto' }}>
                    {timeAgo(c.crystallized_at)}
                  </span>
                </div>
                <div
                  style={{
                    color: 'var(--sp-text)',
                    fontSize: 12,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '100%',
                  }}
                  title={c.content_preview}
                >
                  {(c.title || c.content_preview).slice(0, 120)}
                  {(c.title || c.content_preview).length > 120 ? '...' : ''}
                </div>
                {c.topic_tags && c.topic_tags.length > 0 && (
                  <div style={{ display: 'flex', gap: 4, marginTop: 2, flexWrap: 'wrap' }}>
                    {c.topic_tags.slice(0, 3).map((tag) => (
                      <span
                        key={tag}
                        className="mono"
                        style={{
                          fontSize: 9,
                          padding: '1px 4px',
                          background: 'var(--sp-bg)',
                          border: '1px solid var(--sp-border)',
                          borderRadius: 3,
                          color: 'var(--sp-text-dim)',
                        }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div style={{ color: 'var(--sp-text-muted)', fontSize: 12 }}>Loading chunks...</div>
        )}
      </div>
    </div>
  );
}

export const codexRecentIngestsWidget: WidgetDefinition = {
  id: 'codex-recent-ingests',
  name: 'Codex Recent Ingests',
  description: 'Last N chunks added to your Codex knowledge base.',
  icon: 'brain',
  category: 'workspace',
  scope: 'tenant',
  defaultSize: { w: 6, h: 5 },
  minSize: { w: 4, h: 3 },
  component: CodexRecentIngestsWidget,
};
